import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, fmtDateShort } from '../utils/dates';
import {
  sportsAdminApi, StatusBadge, ApproveModal, SuperAdminShell, STATUS_CONFIG, PLAN_CONFIG,
} from './SportsSuperAdminPage';

// /sports/super-admin/tenants/:orgId — ONE home for everything about a tenant:
// overview, subscription & billing controls, payment history, courts, bookings.
// Replaces the old inline expanded card + detail modal (IA Phase 2).
export default function SportsSuperAdminTenantDetailPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('agripos_token');
  const api = useMemo(() => sportsAdminApi(token), [token]);

  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null); // 'notfound' | 'failed' | null
  const [showApprove, setShowApprove] = useState(false);
  const [saving, setSaving] = useState(null);

  // Control fields — synced from the org on every successful load so an action's
  // result is immediately reflected.
  const [rate, setRate] = useState('');
  const [trialDays, setTrialDays] = useState('14');
  const [renewDays, setRenewDays] = useState('30');
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryNote, setExpiryNote] = useState('');
  const [planSel, setPlanSel] = useState('tenant');
  const [planExpiry, setPlanExpiry] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [pricingMode, setPricingMode] = useState('custom');
  const [perCourtRate, setPerCourtRate] = useState(500);
  const [minBookings, setMinBookings] = useState(4);
  const [trialLimit, setTrialLimit] = useState(1);

  const load = useCallback(() => {
    setError(null);
    api.get(`/api/sports/admin/orgs/${orgId}`)
      .then(r => {
        setDetail(r.data);
        const o = r.data.org || {};
        setRate(o.monthly_rate ?? '');
        setPricingMode(o.pricing_mode || 'custom');
        setPerCourtRate(o.per_court_rate ?? 500);
        setMinBookings(o.billable_min_bookings ?? 4);
        setTrialLimit(o.trial_event_limit ?? 1);
        setPlanSel(o.plan || 'tenant');
        setExpiryDate(o.next_renewal_date ? o.next_renewal_date.slice(0, 10) : '');
      })
      .catch(e => setError(e.response?.status === 404 ? 'notfound' : 'failed'));
  }, [api, orgId]);

  useEffect(() => { load(); }, [load]);

  const act = async (endpoint, body = {}, successMsg) => {
    setSaving(endpoint);
    try {
      await api.put(`/api/sports/admin/orgs/${orgId}/${endpoint}`, body);
      toast.success(successMsg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); }
  };

  // Support session — mint a short-lived owner token (no credentials involved).
  // `open` → new tab as the tenant; otherwise the hand-off link goes to the
  // clipboard (e.g. to paste into an AI debugging session).
  const accessCustomer = async (open) => {
    setSaving('impersonate');
    try {
      const r = await api.post(`/api/sports/admin/orgs/${orgId}/impersonate`);
      const url = `${window.location.origin}/sports/dashboard#support_token=${encodeURIComponent(r.data.token)}`;
      if (open) {
        window.open(url, '_blank', 'noopener');
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Support link copied — valid 2 hours, one tab per paste');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start support session'); }
    finally { setSaving(null); }
  };

  const inp = { background: '#111', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none' };
  const rowStyle = { background: '#0A0A0A', border: '1px solid #1A1A1A', color: '#A3A3A3', borderRadius: 6, padding: '6px 10px', fontSize: 12 };
  const sectionCard = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '16px 20px' };
  const sectionTitle = { color: '#555', fontWeight: 700 };

  if (error) {
    return (
      <SuperAdminShell title="Tenant" active="tenants">
        <div style={{ padding: '60px 28px', textAlign: 'center', color: '#555' }}>
          <p style={{ fontSize: 14, marginBottom: 14 }}>
            {error === 'notfound' ? "This tenant doesn't exist — it may have been removed." : "Couldn't load this tenant — check your connection."}
          </p>
          {error === 'failed' && (
            <button onClick={load} style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#A3A3A3', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginRight: 8 }}>
              Try again
            </button>
          )}
          <button onClick={() => navigate('/sports/super-admin/tenants')}
            style={{ background: '#E63946', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Back to tenants
          </button>
        </div>
      </SuperAdminShell>
    );
  }

  if (!detail) {
    return (
      <SuperAdminShell title="Tenant" active="tenants">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} />
        </div>
      </SuperAdminShell>
    );
  }

  const { org, courts, recent_bookings, owner, invoices = [] } = detail;
  const p = PLAN_CONFIG[org.plan] || PLAN_CONFIG.tenant;

  return (
    <SuperAdminShell title={org.name} active="tenants"
      headerRight={
        <button onClick={load} style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }>
      {showApprove && <ApproveModal org={org} api={api} onDone={() => { setShowApprove(false); load(); }} />}

      <div style={{ padding: '16px 16px 28px 16px', display: 'grid', gap: 16, maxWidth: 1000 }}>
        {/* Breadcrumb + identity */}
        <div>
          <p className="text-xs" style={{ color: '#555' }}>
            <button onClick={() => navigate('/sports/super-admin/tenants')}
              style={{ background: 'transparent', border: 'none', color: '#E63946', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 700 }}>
              Tenants
            </button>
            {' / '}{org.name}
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <StatusBadge status={org.account_status} />
            <span style={{ fontSize: 11, color: p.color, fontWeight: 700 }}>{p.label}</span>
            <span style={{ fontSize: 12, color: '#555' }}>/{org.slug} · {org.owner_email}</span>
            {org.account_status === 'pending' && (
              <button onClick={() => setShowApprove(true)} data-testid={`admin-detail-approve-${org.id}`}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#00E676', color: '#000', border: 'none', cursor: 'pointer' }}>
                Approve
              </button>
            )}
            {org.account_status !== 'pending' && (
              <>
                <button onClick={() => accessCustomer(true)} disabled={saving === 'impersonate'}
                  data-testid={`admin-detail-access-${org.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                  style={{ background: '#FFB300', color: '#000', border: 'none', cursor: 'pointer' }}>
                  Access customer
                </button>
                <button onClick={() => accessCustomer(false)} disabled={saving === 'impersonate'}
                  data-testid={`admin-detail-support-link-${org.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                  style={{ background: '#1A1A1A', color: '#FFB300', border: '1px solid #FFB30040', cursor: 'pointer' }}>
                  Copy support link
                </button>
              </>
            )}
          </div>
        </div>

        {/* Overview */}
        <div style={sectionCard} data-testid="admin-detail-overview">
          <p className="text-xs uppercase tracking-wide mb-3" style={sectionTitle}>Overview</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Show only what is ACTUALLY IN EFFECT (2026-08-18). Both a monthly
                rate and a per-court rate exist on every org, but only ONE is used:
                per-court mode ignores monthly_rate entirely. Showing both made the
                panel read as "₱1,000/mo" for an account that would really be billed
                ₱500/court. Same for the period: a trial is governed by trial_ends_at,
                a tenant by next_renewal_date — label whichever one is live. */}
            {[
              ['Plan', PLAN_CONFIG[org.plan]?.label || org.plan],
              ['Status', STATUS_CONFIG[org.account_status]?.label || org.account_status],
              org.pricing_mode === 'per_court'
                ? ['Billing', `₱${Number(org.per_court_rate || 500).toLocaleString()}/court · usage-based`]
                : ['Monthly Rate', org.monthly_rate > 0 ? `₱${org.monthly_rate?.toLocaleString()}` : 'Not set'],
              ['Max Courts', org.max_courts ?? 'Unlimited'],
              org.account_status === 'trial'
                ? ['Trial ends (in effect)', fmtDate(org.trial_ends_at) || '—']
                : ['Expires (in effect)', fmtDate(org.next_renewal_date) || '—'],
              ['Last Renewed', fmtDate(org.last_renewed_at) || '—'],
              org.account_status === 'trial'
                ? ['Renewal date', fmtDate(org.next_renewal_date) || '—']
                : ['Trial Ends', fmtDate(org.trial_ends_at) || '—'],
              ['Registered', fmtDate(org.created_at) || '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#0A0A0A', borderRadius: 8, padding: '8px 12px' }}>
                <p className="text-xs uppercase tracking-wide" style={{ color: '#555' }}>{k}</p>
                <p className="text-sm font-semibold mt-0.5" style={{ color: '#ccc' }}>{v}</p>
              </div>
            ))}
          </div>
          {/* Plan/status mismatches used to be silently unswept — surface them. */}
          {org.account_status === 'trial' && org.plan === 'tenant' && (
            <p className="text-xs mt-3 rounded-lg px-3 py-2" data-testid="admin-detail-state-warning"
              style={{ background: '#1A1206', border: '1px solid #FF980055', color: '#FFB74D' }}>
              Plan is <strong>Tenant</strong> but status is still <strong>Trial</strong> — access is
              governed by the TRIAL end date until the nightly sweep converts this account to a
              per-court tenant with a {' '}grace window. To act now, use <strong>Set expiry</strong>{' '}
              (it sets the plan, status and date together).
            </p>
          )}
          {org.account_status === 'active' && org.plan === 'trial' && (
            <p className="text-xs mt-3 rounded-lg px-3 py-2"
              style={{ background: '#1A1206', border: '1px solid #FF980055', color: '#FFB74D' }}>
              Plan is <strong>Trial</strong> but status is <strong>Active</strong> with no expiry —
              this account has unlimited free access. Set a plan and expiry if that isn't intended.
            </p>
          )}
          {owner?.name && (
            <p className="text-xs mt-3" style={{ color: '#737373' }}>
              Owner: <span style={{ color: '#ccc' }}>{owner.name}</span> · {owner.email} {owner.phone ? `· ${owner.phone}` : ''}
            </p>
          )}
          {org.admin_notes && (
            <p className="text-xs mt-2" style={{ color: '#737373' }}>Notes: <span style={{ color: '#ccc' }}>{org.admin_notes}</span></p>
          )}
        </div>

        {/* Subscription & billing */}
        <div style={sectionCard} data-testid="admin-detail-billing">
          <p className="text-xs uppercase tracking-wide mb-3" style={sectionTitle}>Subscription &amp; Billing</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Record renewal */}
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Record renewal (days to extend)</p>
              <div className="flex gap-2">
                <input type="number" min="1" value={renewDays} onChange={e => setRenewDays(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button onClick={() => act('renew', { days: parseInt(renewDays) }, 'Renewal recorded')} disabled={saving === 'renew'}
                  data-testid={`sports-admin-renew-${org.id}`}
                  style={{ background: '#00E67614', color: '#00E676', border: '1px solid #00E67630', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Renew
                </button>
              </div>
              <p style={{ fontSize: 10, color: '#444', marginTop: 6 }}>Extends from the current expiry, or from today if already lapsed — same rule as online renewals.</p>
            </div>

            {/* Set expiry (override) */}
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Set expiry date (override)</p>
              <div className="flex gap-2">
                <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                  data-testid={`sports-admin-expiry-date-${org.id}`}
                  style={{ ...inp, flex: 1, colorScheme: 'dark' }} />
                <button onClick={() => { if (!expiryDate) { toast.error('Pick a date first'); return; }
                    act('set-expiry', { date: expiryDate, note: expiryNote }, `Expiry set to ${fmtDate(expiryDate)}`); }}
                  disabled={saving === 'set-expiry'}
                  data-testid={`sports-admin-set-expiry-${org.id}`}
                  style={{ background: '#FFD70014', color: '#FFD700', border: '1px solid #FFD70030', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Set expiry
                </button>
              </div>
              <input type="text" value={expiryNote} onChange={e => setExpiryNote(e.target.value)}
                placeholder="Reason (optional) — e.g. courtesy extension"
                style={{ ...inp, marginTop: 6, width: '100%', fontSize: 12, padding: '6px 10px' }} />
              <p style={{ fontSize: 10, color: '#444', marginTop: 6 }}>Sets any expiry directly, no payment recorded. A future date reactivates a graced or suspended account.</p>
            </div>

            {/* Monthly rate */}
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Monthly Rate (₱)</p>
              <div className="flex gap-2">
                <input type="number" min="0" value={rate} onChange={e => setRate(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button onClick={() => act('update', { monthly_rate: parseFloat(rate || 0) }, 'Rate updated')} disabled={saving === 'update'}
                  style={{ background: '#2A2A2A', color: '#A3A3A3', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Set
                </button>
              </div>
            </div>

            {/* Trial */}
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Start Trial (days)</p>
              <div className="flex gap-2">
                <input type="number" min="1" value={trialDays} onChange={e => setTrialDays(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button onClick={() => act('set-trial', { trial_days: parseInt(trialDays) }, 'Trial started')} disabled={saving === 'set-trial'}
                  data-testid={`sports-admin-trial-${org.id}`}
                  style={{ background: '#00BCD414', color: '#00BCD4', border: '1px solid #00BCD430', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Start
                </button>
              </div>
            </div>

            {/* Plan */}
            <div className="sm:col-span-2" style={{ borderTop: '1px solid #1A1A1A', paddingTop: 12 }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Plan</p>
              <div className="flex gap-2 flex-wrap items-end">
                <select value={planSel} onChange={e => setPlanSel(e.target.value)}
                  data-testid={`sports-admin-plan-${org.id}`} style={{ ...inp, fontSize: 12 }}>
                  <option value="founder">Founder — free, never expires</option>
                  <option value="tenant">Tenant — paid subscription</option>
                  <option value="trial">Trial</option>
                </select>
                {planSel === 'tenant' && org.plan !== 'tenant' && (
                  <div>
                    <p style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>Subscription expires</p>
                    <input type="date" value={planExpiry} onChange={e => setPlanExpiry(e.target.value)}
                      data-testid={`sports-admin-plan-expiry-${org.id}`} style={{ ...inp, fontSize: 12, colorScheme: 'dark' }} />
                  </div>
                )}
                <button onClick={() => {
                    if (planSel === org.plan) { toast.error('Plan is unchanged'); return; }
                    if (planSel === 'tenant' && org.plan !== 'tenant' && !planExpiry) { toast.error('Pick the expiry date first'); return; }
                    const body = { plan: planSel };
                    if (planSel === 'tenant' && planExpiry) body.next_renewal_date = planExpiry;
                    act('update', body, 'Plan updated');
                  }} disabled={saving === 'update'}
                  data-testid={`sports-admin-save-plan-${org.id}`}
                  style={{ background: '#2A2A2A', color: '#A3A3A3', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Save plan
                </button>
              </div>
              {planSel === 'tenant' && org.plan === 'founder' && (
                <p style={{ fontSize: 10, color: '#FF9800', marginTop: 6 }}>Moving a founder to paid — they start renewing from the date you pick.</p>
              )}
            </div>

            {/* Billing model */}
            <div className="sm:col-span-2" style={{ borderTop: '1px solid #1A1A1A', paddingTop: 12 }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Billing Model</p>
              <div className="flex gap-2 flex-wrap items-end">
                <div>
                  <p style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>Mode</p>
                  <select value={pricingMode} onChange={e => setPricingMode(e.target.value)}
                    data-testid={`sports-admin-pricing-mode-${org.id}`} style={{ ...inp, fontSize: 12 }}>
                    <option value="custom">Custom monthly rate</option>
                    <option value="per_court">Per court (usage-based)</option>
                  </select>
                </div>
                <div>
                  <p style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>₱ / court / mo</p>
                  <input type="number" min="0" value={perCourtRate} onChange={e => setPerCourtRate(e.target.value)}
                    data-testid={`sports-admin-per-court-rate-${org.id}`} style={{ ...inp, width: 90, fontSize: 12 }} />
                </div>
                <div>
                  <p style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>Min bookings to bill</p>
                  <input type="number" min="1" value={minBookings} onChange={e => setMinBookings(e.target.value)}
                    data-testid={`sports-admin-min-bookings-${org.id}`} style={{ ...inp, width: 80, fontSize: 12 }} />
                </div>
                <div>
                  <p style={{ fontSize: 10, color: '#555', marginBottom: 4 }}>Trial event limit</p>
                  <input type="number" min="1" value={trialLimit} onChange={e => setTrialLimit(e.target.value)}
                    data-testid={`sports-admin-trial-limit-${org.id}`} style={{ ...inp, width: 70, fontSize: 12 }} />
                </div>
                <button onClick={() => act('update', {
                    pricing_mode: pricingMode,
                    per_court_rate: parseFloat(perCourtRate || 0),
                    billable_min_bookings: parseInt(minBookings || 1),
                    trial_event_limit: parseInt(trialLimit || 1),
                  }, 'Billing model saved')} disabled={saving === 'update'}
                  data-testid={`sports-admin-save-billing-${org.id}`}
                  style={{ background: '#E6394614', color: '#E63946', border: '1px solid #E6394630', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Save Billing
                </button>
              </div>
              <p style={{ fontSize: 10, color: '#444', marginTop: 6 }}>
                Per-court mode bills only courts with ≥ min bookings in the last 30 days (min 1 court). Trial event limit caps tournaments + open play combined.
              </p>
            </div>

            {/* Ball machine (K6) — devices are managed in Super Admin → Devices and LINKED to a tenant there;
                this flag only gates the tenant's read-only "Ball machine" page (Phase 2). */}
            <div className="sm:col-span-2" style={{ borderTop: '1px solid #1A1A1A', paddingTop: 12 }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Ball machine page</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span style={{ fontSize: 12, color: org.devices_enabled ? '#00E676' : '#555' }}>
                  {org.devices_enabled ? 'Enabled — the tenant sees the read-only Ball machine page for devices linked to them' : 'Off — no Ball machine page for this tenant (link devices under Devices)'}
                </span>
                <button onClick={() => act('update', { devices_enabled: !org.devices_enabled }, org.devices_enabled ? 'Kiosk devices disabled' : 'Kiosk devices enabled')}
                  disabled={saving === 'update'}
                  data-testid={`sports-admin-devices-toggle-${org.id}`}
                  style={{ background: org.devices_enabled ? '#2A2A2A' : '#00E67614', color: org.devices_enabled ? '#A3A3A3' : '#00E676',
                    border: `1px solid ${org.devices_enabled ? '#2A2A2A' : '#00E67630'}`, borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  {org.devices_enabled ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>

            {/* Account control */}
            <div className="sm:col-span-2" style={{ borderTop: '1px solid #1A1A1A', paddingTop: 12 }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Account Control</p>
              <div className="flex gap-2 flex-wrap">
                {org.account_status !== 'active' && (
                  <button onClick={() => act('activate', {}, 'Activated')} disabled={saving === 'activate'}
                    data-testid={`sports-admin-activate-${org.id}`}
                    style={{ background: '#00E67614', color: '#00E676', border: '1px solid #00E67630', borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    Activate
                  </button>
                )}
                {org.account_status !== 'suspended' && (
                  <button onClick={() => act('suspend', { reason: 'Manual suspend by admin' }, 'Suspended')} disabled={saving === 'suspend'}
                    data-testid={`sports-admin-suspend-${org.id}`}
                    style={{ background: '#FF2D5514', color: '#FF2D55', border: '1px solid #FF2D5530', borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    Suspend
                  </button>
                )}
                {org.account_status !== 'deactivated' && (
                  <button onClick={() => act('deactivate', {}, 'Deactivated')} disabled={saving === 'deactivate'}
                    data-testid={`sports-admin-deactivate-${org.id}`}
                    style={{ background: '#2A2A2A', color: '#555', border: '1px solid #2A2A2A', borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment history */}
        <div style={sectionCard} data-testid="admin-detail-payments">
          <p className="text-xs uppercase tracking-wide mb-2" style={sectionTitle}>Payment History ({invoices.length})</p>
          {invoices.length === 0 ? (
            <p style={{ color: '#444', fontSize: 13 }}>No online payments yet. Manual renewals appear in the Overview fields, not here.</p>
          ) : (
            <div className="space-y-1">
              {invoices.map(inv => (
                <div key={inv.id} style={{ ...rowStyle, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{fmtDateShort(inv.paid_at || inv.created_at)} · ₱{Number(inv.amount_php || 0).toLocaleString()}
                    {inv.pricing_mode === 'per_court' ? ` · ${inv.court_count} court${inv.court_count === 1 ? '' : 's'}` : ''}</span>
                  <span style={{ color: inv.status === 'paid' ? '#00E676' : inv.status === 'pending' ? '#FFB300' : '#555', textTransform: 'capitalize' }}>
                    {inv.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Courts */}
        <div style={sectionCard} data-testid="admin-detail-courts">
          <p className="text-xs uppercase tracking-wide mb-2" style={sectionTitle}>Courts ({courts.length})</p>
          {courts.length === 0 ? <p style={{ color: '#444', fontSize: 13 }}>No courts yet</p> : (
            <div className="space-y-1">
              {courts.map(c => (
                <div key={c.id} style={{ ...rowStyle, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{c.name}</span>
                  <span style={{ color: '#555', textTransform: 'capitalize' }}>{c.sport} · {c.active ? 'Active' : 'Inactive'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent bookings */}
        <div style={sectionCard} data-testid="admin-detail-bookings">
          <p className="text-xs uppercase tracking-wide mb-2" style={sectionTitle}>Recent Bookings ({recent_bookings.length})</p>
          {recent_bookings.length === 0 ? <p style={{ color: '#444', fontSize: 13 }}>No bookings yet</p> : (
            <div className="space-y-1">
              {recent_bookings.slice(0, 10).map(b => (
                <div key={b.id} style={{ ...rowStyle, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{b.customer?.name || '?'} · {fmtDateShort(b.date)}</span>
                  <span style={{ color: b.payment_status === 'paid' ? '#00E676' : '#E63946' }}>
                    {b.status} · {b.payment_status || '?'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SuperAdminShell>
  );
}
